import { useId } from 'react';
import { CheckIcon, CrossIcon } from '@/components/icons';
import { cn } from '@/lib/cn';

type AnswerOptionsProps = {
  legend: string;
  options: string[];
  value: number | null;
  onChange: (index: number) => void;
  /** After checking: the correct index, to colour the selected option. */
  revealed?: { answer: number } | null;
  disabled?: boolean;
  columns?: 1 | 2;
  mono?: boolean;
  className?: string;
};

/** A fieldset of radio labels (48px, mono). Correct/wrong shown by border, colour, icon AND text. */
export function AnswerOptions({
  legend,
  options,
  value,
  onChange,
  revealed,
  disabled,
  columns = 2,
  mono = true,
  className,
}: AnswerOptionsProps) {
  const name = useId();
  return (
    <fieldset
      className={cn(
        'm-0 grid min-w-0 gap-2.5 border-0 p-0',
        columns === 2 ? 'sm:grid-cols-2' : '',
        className,
      )}
    >
      <legend className="mb-3 p-0 text-sm text-ink-muted">{legend}</legend>
      {options.map((option, i) => {
        const selected = value === i;
        const correct = revealed && selected && i === revealed.answer;
        const wrong = revealed && selected && i !== revealed.answer;
        return (
          <label
            key={i}
            className={cn(
              'flex min-h-12 cursor-pointer items-center gap-2.5 rounded-md px-4 py-2 text-sm',
              mono && 'font-mono',
              correct
                ? 'border-2 border-success bg-success-soft'
                : wrong
                  ? 'border-2 border-danger bg-danger-soft'
                  : selected
                    ? 'border-2 border-ink'
                    : 'border border-line hover:border-line-strong',
              disabled && 'cursor-default',
            )}
          >
            <input
              type="radio"
              name={name}
              className="m-0 size-4 shrink-0 accent-ink"
              checked={selected}
              disabled={disabled}
              onChange={() => onChange(i)}
            />
            <span className="flex-1 break-words">{option}</span>
            {correct && (
              <span className="flex items-center gap-1 font-sans text-xs font-semibold text-success-ink">
                <CheckIcon size={16} className="text-success" /> Correct
              </span>
            )}
            {wrong && (
              <span className="flex items-center gap-1 font-sans text-xs font-semibold text-danger-ink">
                <CrossIcon size={16} className="text-danger" /> Not this one
              </span>
            )}
          </label>
        );
      })}
    </fieldset>
  );
}
